import './styles/card.scss';

import { Public, Email, Person, Phone, Place } from '@mui/icons-material';
import {
  Divider,
  Paper,
  Typography,
  Grid,
  Skeleton,
  Grid2,
  Button,
  Avatar,
  Tooltip
} from '@mui/material';
import { Stack } from '@mui/system';
import { BillingPermissions } from '@thro-types/access-handler';
import { CreateOrPutCustomerInput, Customer, Invoice } from '@thro-types/billing';

import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { validate as uuidValidate } from 'uuid';

import { translations } from 'locales/i18n';
import { countries, countryToFlag } from 'utils/Countries';
import { useAuthContext } from 'utils/hooks/useAuthContext';

import { CustomerDrawerActions } from '../Customer/CustomerDrawer/customerDrawerSlice';

interface Props {
  data:
    | (Customer & { invoice?: Invoice; shouldHide?: boolean })
    | CreateOrPutCustomerInput
    | undefined;
  renderSkeleton?: boolean;
  hideEdit?: boolean;
  showEmptyPhone?: boolean;
  isClickable?: boolean;
}

export const InfoCustomerCard = ({
  data,
  renderSkeleton = false,
  hideEdit = false,
  showEmptyPhone = false,
  isClickable = false
}: Props): React.ReactElement => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { permissions } = useAuthContext();
  const notAvailableText = t(translations.common.notAvailable());

  const topContainer = [
    {
      icon: <Public />,
      content: (
        <Stack gap={1} direction={'row'}>
          {data?.country}
          <span>{countryToFlag(countries.find(c => c.code === data?.country)?.code || '')}</span>
        </Stack>
      )
    },
    {
      icon: <Place />,
      content: (
        <div
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {data?.zip || ''} {data?.city || ''} {data?.address || ''}
        </div>
      )
    }
  ];

  const bottomContainer = [
    {
      icon: (
        <Tooltip title={t(translations.pages.billing.invoiceCreate.customerId())}>
          <Avatar
            sx={{
              bgcolor: 'white',
              color: 'black',
              border: '2px solid black',
              width: 23,
              height: 23,
              fontSize: 14
            }}
          >
            <Typography
              sx={{
                fontWeight: 'bold',
                fontSize: '0.6rem',
                padding: '1px'
              }}
              variant="caption"
            >
              ZP
            </Typography>
          </Avatar>
        </Tooltip>
      ),
      content: `${uuidValidate(data?.customerId) ? '' : data?.customerId || ''}`
    },
    { icon: <Person />, content: `${data?.firstName || ''} ${data?.lastName || ''}` },
    {
      icon: <Email />,
      content: `${data?.email || notAvailableText}`
    }
  ];

  const gridStyle = isClickable && {
    transition: 'background-color 0.2s ease',
    ...(isClickable && {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: 'action.hover'
      }
    })
  };

  if (data?.phone || showEmptyPhone) {
    bottomContainer.push({ icon: <Phone />, content: `${data?.phone || notAvailableText}` });
  }

  if (renderSkeleton) {
    return <Skeleton variant="rounded" height={209} />;
  } else {
    return (
      <Grid2
        component={Paper}
        paddingBottom={2}
        paddingX={2}
        sx={{
          ...gridStyle
        }}
      >
        <Grid2 container justifyContent={'center'} paddingY={1} className="Information">
          <Grid2 container>
            <Typography variant="h5">{data?.companyName}</Typography>
          </Grid2>
        </Grid2>
        {informationContainer(topContainer)}
        <Grid2 container justifyContent={'center'} paddingY={2}>
          <Grid2 size={{ xs: 10 }}>
            <Divider />
          </Grid2>
        </Grid2>
        {informationContainer(bottomContainer)}
        {!hideEdit &&
          permissions.includes(BillingPermissions.BILLING_CUSTOMER_WRITE) &&
          data &&
          'id' in data && (
            <Grid2 container alignItems={'center'} justifyContent={'center'} paddingTop={2}>
              <Button
                onClick={e => {
                  e.preventDefault();
                  dispatch(CustomerDrawerActions.setCustomer(data));
                  dispatch(CustomerDrawerActions.open());
                }}
              >
                {t(translations.common.edit())}
              </Button>
            </Grid2>
          )}
      </Grid2>
    );
  }
};

interface InformationProps {
  icon: JSX.Element;
  content: ReactNode;
}

const informationContainer = (informations: InformationProps[]) => {
  return (
    <Grid2 container alignContent={'center'} spacing={1}>
      {informations.map((info, index) => (
        <Grid2 container alignContent={'flex-end'} key={index} size={{ xs: 12 }}>
          <Grid2 size={{ xs: 1 }}>{info.icon}</Grid2>
          <Grid2 paddingLeft={1} className="text" maxWidth={'20em'} size={{ xs: 11 }}>
            {info.content}
          </Grid2>
        </Grid2>
      ))}
    </Grid2>
  );
};

export const InformationCustomerPart = ({
  data,
  renderSkeleton = false
}: Props): React.ReactElement => {
  const bottomContainer = [
    { icon: <Person />, content: `${data?.firstName} ${data?.lastName}` },
    { icon: <Email />, content: `${data?.email}` }
  ];

  if (data?.phone) {
    bottomContainer.push({ icon: <Phone />, content: `${data?.phone}` });
  }

  if (renderSkeleton) {
    return <Skeleton variant="rectangular" height={72} />;
  } else {
    return (
      <Grid container component={Paper}>
        <Grid item xs className="Information">
          <Grid container paddingBottom={1} paddingLeft={'1em'}>
            <Typography variant="h5">{data?.companyName}</Typography>
          </Grid>
          <Grid container paddingBottom={1} paddingLeft={'0.75em'}>
            {informationRow([
              {
                icon: <Place />,
                content: `${data?.country} ${data?.zip} ${data?.city} ${data?.address}`
              }
            ])}
          </Grid>
        </Grid>
        <Grid item style={{ marginTop: '1em', marginBottom: '1em' }}>
          <Divider orientation="vertical" />
        </Grid>
        <Grid item xs paddingY={1}>
          {informationContainer(bottomContainer)}
        </Grid>
      </Grid>
    );
  }
};

const informationRow = (informations: InformationProps[]) => {
  return (
    <Grid container alignContent={'center'} spacing={2} justifyContent={'space-between'}>
      {informations.map((info, index) => (
        <Grid item>
          <Grid container alignContent={'flex-end'} key={index}>
            {info.icon}
            <Grid paddingLeft={1} item>
              {info.content}
            </Grid>
          </Grid>
        </Grid>
      ))}
    </Grid>
  );
};
